export type License = {
  id?: number;
  state: string;
  number: string;
  expiry_date: string;
  insurance_carriers: UpdatedInsuranceCarrier[];
};

export type ProviderListTableLicense = {
  state: string;
  number: string;
  expiry_date: string;
  insurance_carriers: ProviderListTableLicenseInsuranceCarrier[];
};

export type ProviderListTableLicenseInsuranceCarrier = {
  id: number;
  name: string;
  payer_id: string;
};

export enum UserRoleName {
  Admin = 'Admin',
  SuperAdmin = 'SuperAdmin',
  OrganizationAdmin = 'ClientAdmin',
  Provider = 'Provider',
  Patient = 'Patient',
  CareAdvisor = 'CareAdvisor',
  Collector = 'Collector',
  Dependent = 'Dependent',
}

/** A client's own sub-group. Belongs to exactly one ClientTag; never hard-deleted, only deactivated. */
export interface ClientDivision {
  id: number;
  name: string;
  is_active: boolean;
}

export interface ClientTag {
  id: number;
  name: string;
  code?: string;
  disable_customerio?: boolean;
  customer_io_exclusive?: boolean;
  customer_io_browser_key?: string | null;
  divisions?: ClientDivision[];
}

export interface ClientBranding {
  organization_name: string;
  logo_url: string;
  primary_color: string;
  secondary_color: string;
  button_color: string;
  button_hover_color?: string | null;
  text_color: string;
  footer_text_color: string;
  legal_text_color: string;
  font_family: string;
  footer_link_text: string;
  uses_custom_logo: boolean;
}

export interface UserRole {
  id: number;
  name: UserRoleName;
  permissions: string;
}

export interface Language {
  id: string;
  name: string;
  locale: string;
}

export interface BasicInfo {
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  preferred_name?: string;
  gender?: string;
  date_of_birth?: string;
  pronouns?: string;
  languages?: Language[] | string[];
  gender_identity?: string;
}

export interface InsuranceThrough {
  first_name?: string;
  date_of_birth?: string;
  gender_identity?: string;
  middle_name?: string;
  last_name?: string;
  address: Address;
}

export interface Address {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  timezone?: string;
}

export interface UpdatedInsuranceCarrier {
  insurance_carrier?: InsuranceCarrier;
  insurance_carrier_id?: number;
  effective_date: string;
  recredential_date: string;
}

export interface InsuranceCarrier {
  id: number;
  name: string;
  payer_id?: string;
}

export interface InsuranceMembership {
  member_id: string;
  group_number?: string;
  insurance_carrier?: InsuranceCarrier;
  insurance_carrier_id?: number;
  phone_number: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  address?: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    country?: string;
  };
}

export interface InsuranceMedia {
  media_id: string;
  type: string;
}

export interface Insurance {
  id?: number;
  user_id?: number;
  insured_by: string;
  insurance_membership?: InsuranceMembership;
  insurance_through?: InsuranceThrough;
  is_current?: boolean;
  state_funded_coverage_status?: string;
  is_secondary?: boolean;
  media?: InsuranceMedia[];
  front_of_card?: InsuranceMedia[];
  back_of_card?: InsuranceMedia[];
}

export interface PaymentProfile {
  full_name: string;
  expiration_month: number;
  expiration_year: number;
  cc_last_four: number;
  billing_address: Address;
  is_active: boolean;
}

export interface PatientProfile {
  insurances: Insurance[];
  payment_profile: PaymentProfile;
  divisions: Division[];
  practices: Practice[];
  address?: Address;
}

export interface OrganizationAdminProfile {
  divisions?: Division[];
  practices?: Practice[];
  address?: Address;
}

export interface ProviderProfile {
  licenses?: License[];
  dea_number?: string;
  national_provider_id?: string;
  credentials?: string;
  education?: string[];
  additional_credentials?: string;
  specialty?: string[];
  biography?: string;
  plan?: string[];
  gender?: string;
  availability?: string[];
  npi?: string;
  status?: string;
  address?: Address;
}
export interface CareAdvisorProfile {
  biography?: string;
  address?: Address;
}

export interface ProfessionalCorporation {
  id: string;
  state: string;
  video_case_enabled: boolean;
  async_case_enabled: boolean;
  second_opinion_case_enabled: boolean;
  phone_case_enabled: boolean;
}

export interface Division {
  id: string;
  name: string;
  is_default?: boolean;
  organization_id?: string;
  domain?: string;
}

export interface Practice {
  id: string;
  name: string;
  is_default?: boolean;
  organization_id?: string;
  domain?: string;
}
export interface Organization {
  id: string;
  name: string;
  credit_card_required?: boolean;
  divisions: Division[];
  practices: Practice[];
  professional_corporations?: ProfessionalCorporation[];
  follow_up_enabled?: boolean;
  care_coordination_enabled?: boolean;
  oral_health_coaching_enabled?: boolean;
  collect_insurance_info?: boolean;
  share_provider_network?: boolean;
  share_care_advisor_network?: boolean;
  uses_custom_logo: boolean;
}

export interface Marker {
  created_at: string;
  id: number;
  type: string;
  user_id: number;
  value: string;
}

export interface UserProfile {
  id: number;
  external_id: string;
  username: string;
  email: string;
  unverified_email?: string;
  status: string;
  /** Present on collector user payloads when the record is soft-deleted (timestamp) vs active (null / omitted). */
  deleted_at?: string | null;
  /** True while the account is inside an active failed-login lockout window; only present on the admin user-detail response. */
  is_locked?: boolean;
  full_name: string;
  phone_number: string;
  picture_media_id: string;
  guardian_id?: number;
  guardian_relationship?: string;
  role: Role;
  type?: Role;
  basic_info: BasicInfo;
  organizations: Organization[];
  patient_profile: PatientProfile;
  provider_profile: ProviderProfile;
  care_advisor_profile: CareAdvisorProfile;
  organization_admin_profile?: OrganizationAdminProfile;
  created_at: string;
  updated_at: string;
  state?: string;
  license_states?: string[];
  specialty?: string;
  plan?: string[];
  availability?: string[];
  languages?: Language[];
  insurance?: Insurance[];
  guardian?: any;
  national_provider_id?: string;
  credentials?: string;
  licenses?: License[];
  address?: Address;
  pharmacy_name?: string;
  pharmacy_street_address?: string;
  pharmacy_city?: string;
  pharmacy_state?: string;
  pharmacy_zip_code?: string;
  pharmacy_phone_number?: string;
  markers?: Marker[];
  clients?: ClientTag[];
  /** The user's assigned client divisions (no is_active flag here — that's only on ClientTag.divisions). */
  divisions?: Array<{ id: number; name: string }>;
  /** D1 Network membership code (e.g. 'd1_provider_network'), or null if not assigned. Provider/CareAdvisor only. */
  network?: string | null;
  /** Nested dependents on collector GET user / list responses (same user schema; use deleted_at for soft-delete). */
  dependents?: UserProfile[];
  email_opt_in?: boolean;
  sms_opt_in?: boolean;
  /** True only when the user has client tags and every one of them has disable_customerio set. */
  disable_customerio?: boolean;
  web_state?: {
    state: object;
  };
  guardian_full_name: string;
}

export interface Role {
  name: string;
}
